/*
  Builder trust banner (sections/builder-trust.liquid).
  <blp-trust-ticker>: the CSS marquee runs until the first drag; then this takes over the same track so it
  follows the finger or mouse (horizontal only, vertical swipes still scroll the page), flings a little, wraps
  over the two copies and carries on at the CSS speed. Arrow keys nudge it. Hover, focus and an open clip hold it.
  A drag never opens a clip. Cards open the clip (youtube-nocookie, start/end) in the section's dialog.
*/
if (!customElements.get('blp-trust-ticker')) {
  customElements.define('blp-trust-ticker', class BlpTrustTicker extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.blp-trust__track');
      if (!this.track) return;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.dialog = document.getElementById(this.dataset.video);
      this.x = 0;
      this.v = 0;
      this.speed = 0;
      this.hover = false;
      this.down = null;
      this.moved = false;
      this.js = false;

      // The loop copy shows on screen half the time, so it must take taps too. Keep it out of the tab order.
      this.querySelectorAll('[data-copy] button, [data-copy] a').forEach((el) => el.setAttribute('tabindex', '-1'));

      this.addEventListener('mouseenter', () => { this.hover = true; });
      this.addEventListener('mouseleave', () => { this.hover = false; });
      this.addEventListener('focusin', () => this.classList.add('is-held'));
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget) && !(this.dialog && this.dialog.open)) this.classList.remove('is-held');
      });
      this.addEventListener('keydown', (e) => {
        if (e.target !== this || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
        e.preventDefault();
        this.takeOver();
        this.x += e.key === 'ArrowRight' ? -180 : 180;
        this.wrap();
        this.paint();
      });

      this.track.addEventListener('pointerdown', (e) => this.onDown(e));
      this.track.addEventListener('pointermove', (e) => this.onMove(e));
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((t) => this.track.addEventListener(t, () => this.onUp()));
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
      this.track.addEventListener('click', (e) => {
        if (this.moved) {
          e.preventDefault();
          e.stopPropagation();
          this.moved = false;
          return;
        }
        const card = e.target.closest('[data-blp-play]');
        if (card) this.play(card);
      }, true);

      if (this.dialog) {
        this.dialog.querySelector('[data-blp-close]').addEventListener('click', () => this.dialog.close());
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
        this.dialog.addEventListener('close', () => {
          this.dialog.querySelector('[data-blp-frame]').replaceChildren();
          this.classList.remove('is-held');
          if (this.opener) this.opener.focus({ preventScroll: true });
        });
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
    }

    setWidth() {
      return this.track.scrollWidth / 2;
    }

    wrap() {
      const w = this.setWidth();
      if (!w) return;
      while (this.x <= -w) this.x += w;
      while (this.x > 0) this.x -= w;
    }

    paint() {
      this.track.style.transform = `translate3d(${this.x}px,0,0)`;
    }

    takeOver() {
      if (this.js) return;
      this.js = true;
      this.x = new DOMMatrixReadOnly(getComputedStyle(this.track).transform).m41;
      const secs = parseFloat(getComputedStyle(this.track).animationDuration) || 40;
      this.speed = this.setWidth() / secs;
      this.classList.add('is-js');
      this.paint();
      this.last = performance.now();
      const tick = (t) => {
        const dt = Math.min(0.05, (t - this.last) / 1000);
        this.last = t;
        if (!this.down) {
          const held = this.hover || this.classList.contains('is-held') || document.hidden;
          if (Math.abs(this.v) > 5) {
            this.x += this.v * dt;
            this.v *= Math.pow(0.04, dt);
          } else if (!held && !this.reduce.matches) {
            this.x -= this.speed * dt;
          }
          this.wrap();
          this.paint();
        }
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    onDown(e) {
      if (e.button || this.reduce.matches) return;
      this.takeOver();
      const now = performance.now();
      this.down = { id: e.pointerId, x0: e.clientX, y0: e.clientY, xs: this.x, lx: e.clientX, lt: now };
      this.moved = false;
      this.v = 0;
    }

    onMove(e) {
      const d = this.down;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.x0;
      const dy = e.clientY - d.y0;
      if (!this.moved) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { this.down = null; return; }
        this.moved = true;
        try { this.track.setPointerCapture(e.pointerId); } catch (_) { /* not capturable */ }
        this.classList.add('is-dragging');
      }
      const now = performance.now();
      this.v = (e.clientX - d.lx) / Math.max(0.008, (now - d.lt) / 1000);
      d.lx = e.clientX;
      d.lt = now;
      this.x = d.xs + dx;
      this.wrap();
      this.paint();
    }

    onUp() {
      if (!this.down) return;
      this.down = null;
      this.classList.remove('is-dragging');
      this.v = Math.max(-2500, Math.min(2500, this.v));
    }

    play(card) {
      const dlg = this.dialog;
      if (!dlg || typeof dlg.showModal !== 'function') return;
      const start = Number(card.dataset.start) || 0;
      const end = Number(card.dataset.end) || 0;
      const short = card.hasAttribute('data-short');
      const q = new URLSearchParams({ autoplay: '1', rel: '0', playsinline: '1', modestbranding: '1' });
      if (start) q.set('start', start);
      if (end > start) q.set('end', end);
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(card.dataset.yt)}?${q}`;
      frame.title = card.dataset.title || '';
      frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      frame.allowFullscreen = true;
      dlg.querySelector('[data-blp-frame]').replaceChildren(frame);
      const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      const time = short ? '' : ` · ${fmt(start)}${end > start ? `–${fmt(end)}` : ''}`;
      dlg.querySelector('[data-blp-caption]').textContent = `${card.dataset.title || ''}${time}`;
      dlg.setAttribute('aria-label', card.dataset.title || '');
      dlg.classList.toggle('is-short', short);
      this.opener = card;
      this.classList.add('is-held');
      dlg.showModal();
    }
  });
}
